import {
  Site,
  SUPPORT_CASE_STATUSES,
  SupportCaseStatus,
  SupportCaseContext,
  SupportCaseEvent,
  SupportReportStatus,
  SupportRoute,
  SupportRouteInfo,
} from '@/app/types/global';

export const POWSTON_SUPPORT_EMAIL = 'service@powston.com';

const UNKNOWN_STATUS_LABEL = 'Status update pending';
const UNKNOWN_STATUS_BADGE = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

const SUPPORT_STATUS_PRESENTATION = {
  sent_to_partner: { label: 'Sent to support provider', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', closed: false },
  sent_to_powston: { label: 'Open with Powston', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', closed: false },
  escalated_to_powston: { label: 'Escalated to Powston', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', closed: false },
  awaiting_customer_confirmation: { label: 'Waiting for your confirmation', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  acknowledged: { label: 'Acknowledged by support', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', closed: false },
  resolved: { label: 'Resolved', badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', closed: true },
  send_failed: { label: 'Delivery failed — please resubmit', badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', closed: false },
  powston_responsibility_accepted: { label: 'With Powston', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', closed: false },
  awaiting_installer: { label: 'Waiting for installer', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  awaiting_manufacturer: { label: 'Waiting for equipment manufacturer', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  awaiting_retailer: { label: 'Waiting for retailer or meter provider', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  awaiting_self_managed_action: { label: 'Waiting for your action', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  awaiting_customer_action: { label: 'Waiting for your action', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  awaiting_owner_nomination: { label: 'Waiting for support provider nomination', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', closed: false },
  evidence_required: { label: 'More evidence required', badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300', closed: false },
  classification_inconclusive: { label: 'Under review', badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300', closed: false },
  closed_without_action: { label: 'Closed without action', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', closed: true },
} satisfies Record<SupportCaseStatus, { label: string; badge: string; closed: boolean }>;

function isKnownSupportStatus(status: string): status is SupportCaseStatus {
  return (SUPPORT_CASE_STATUSES as readonly string[]).includes(status);
}

export function supportStatusLabel(status: string, organisationName?: string | null): string {
  if (status === 'sent_to_partner') {
    return organisationName?.trim()
      ? `Sent to ${organisationName.trim()}`
      : SUPPORT_STATUS_PRESENTATION.sent_to_partner.label;
  }
  if (status === 'escalated_to_powston' && organisationName?.trim()) {
    return `Escalated to Powston by ${organisationName.trim()}`;
  }
  return isKnownSupportStatus(status)
    ? SUPPORT_STATUS_PRESENTATION[status].label
    : UNKNOWN_STATUS_LABEL;
}

export function supportStatusBadge(status: string): string {
  return isKnownSupportStatus(status)
    ? SUPPORT_STATUS_PRESENTATION[status].badge
    : UNKNOWN_STATUS_BADGE;
}

export function isClosedSupportStatus(status: string): boolean {
  return isKnownSupportStatus(status) && SUPPORT_STATUS_PRESENTATION[status].closed;
}

export type SupportResponsibility = {
  key: 'hardware' | 'network' | 'metering' | 'trading' | 'platform';
  label: string;
  owner: string;
  explanation: string;
};

/**
 * One shared responsibility model for every support surface. The site's
 * resolved support owner remains the first contact; this list explains which
 * specialist that owner will coordinate after the evidence has been triaged.
 */
export const SUPPORT_RESPONSIBILITIES: readonly SupportResponsibility[] = [
  {
    key: 'hardware',
    label: 'Battery, inverter and firmware',
    owner: 'Installer or equipment manufacturer',
    explanation: 'Device faults, firmware limits and onsite equipment work.',
  },
  {
    key: 'network',
    label: 'Home network and local connection',
    owner: 'Installer or local IT support',
    explanation: 'Router, Wi-Fi, Ethernet, Modbus and controller connectivity.',
  },
  {
    key: 'metering',
    label: 'Retailer, tariff and meter data',
    owner: 'Retailer, metering provider or DNSP',
    explanation: 'NMI, billing, tariff, meter exchange and settlement records.',
  },
  {
    key: 'trading',
    label: 'Trading decision or command evidence',
    owner: 'Powston',
    explanation: 'Why a decision was made, whether a command was issued and how it was verified.',
  },
  {
    key: 'platform',
    label: 'Powston app and platform',
    owner: 'Powston',
    explanation: 'App, service, trading engine, data pipeline and control-logic faults.',
  },
] as const;

const ROUTES = new Set<SupportRoute>([
  'powston_installed',
  'partner_managed',
  'self_service',
  'control_only',
  'unassigned',
]);

function routeFromSite(site?: Site | null): SupportRoute {
  const route = site?.support_route;
  return route && ROUTES.has(route) ? route : 'unassigned';
}

function organisationForRoute(route: SupportRoute, site?: Site | null): string {
  if (route === 'powston_installed' || route === 'self_service') return 'Powston';
  return site?.support_organisation_name?.trim()
    || site?.system_supplier_name?.trim()
    || site?.installer_organisation_name?.trim()
    || '';
}

/**
 * Resolve the current owner with explicit precedence:
 *
 * 1. The backend's resolved /support_route response is authoritative.
 * 2. Site-level support fields are a resilient display fallback.
 * 3. Incomplete/unknown data is unassigned and must show a configure action.
 *
 * Named partner/control-only routes are never accepted without both a name and
 * contact email. That prevents the UI saying an unnamed/unreachable party owns
 * support while another component shows the unassigned warning.
 */
export function resolveSupportOwner(
  resolved: Partial<SupportRouteInfo> | null | undefined,
  site?: Site | null,
): SupportRouteInfo {
  const candidateRoute = resolved?.route && ROUTES.has(resolved.route)
    ? resolved.route
    : routeFromSite(site);
  const organisationName = resolved?.organisation_name?.trim()
    || organisationForRoute(candidateRoute, site);
  const powstonOwned = candidateRoute === 'powston_installed' || candidateRoute === 'self_service';
  const email = powstonOwned
    ? (resolved?.email?.trim() || POWSTON_SUPPORT_EMAIL)
    : (resolved?.email?.trim() || site?.support_email?.trim() || '');
  const namedRouteIncomplete = (candidateRoute === 'partner_managed' || candidateRoute === 'control_only')
    && (!organisationName || !email);
  const route: SupportRoute = namedRouteIncomplete ? 'unassigned' : candidateRoute;

  return {
    site_id: Number(resolved?.site_id ?? site?.id ?? 0),
    route,
    organisation_name: route === 'unassigned' ? '' : organisationName,
    email: route === 'unassigned' ? '' : email,
    phone: resolved?.phone ?? site?.support_phone ?? null,
    portal_url: resolved?.portal_url ?? site?.support_portal_url ?? null,
    powston_escalation_enabled:
      route === 'unassigned'
        ? false
        : resolved?.powston_escalation_enabled
          ?? site?.powston_escalation_enabled
          ?? false,
    source: resolved?.source ?? (site?.support_route ? 'site' : 'default'),
    confirmed_at: resolved?.confirmed_at ?? site?.support_route_confirmed_at ?? null,
    policy_version: resolved?.policy_version ?? site?.support_policy_version ?? null,
    installer_organisation_name:
      resolved?.installer_organisation_name ?? site?.installer_organisation_name ?? null,
    system_supplier_name: resolved?.system_supplier_name ?? site?.system_supplier_name ?? null,
    acknowledgement_text: resolved?.acknowledgement_text ?? '',
    acknowledged: resolved?.acknowledged ?? false,
  };
}

export function supportCaseReference(report: Pick<SupportReportStatus, 'id' | 'reference'>): string {
  if (report.reference?.trim()) return report.reference.trim().toUpperCase();
  return `SUP-${String(report.id).padStart(6, '0')}`;
}

export function supportSeverity(report: SupportReportStatus): 'critical' | 'normal' {
  return report.severity === 'critical' || report.is_safety ? 'critical' : 'normal';
}

export type SupportTimelineItem = {
  id: string;
  label: string;
  occurred_at: string;
  detail?: string | null;
};

export function supportEventLabel(event: SupportCaseEvent): string {
  const nextStatus = typeof event.new_value?.status === 'string'
    ? event.new_value.status
    : null;
  switch (event.event_type) {
    case 'intake': return 'Case created';
    case 'delivery': {
      switch (event.details?.outcome) {
        case 'sent': return 'Sent to support owner';
        case 'failed': return 'Delivery failed';
        case 'cancelled': return 'Delivery cancelled';
        case 'pending': return 'Delivery in progress';
        default: return 'Delivery status uncertain';
      }
    }
    case 'triage': return 'Case reviewed';
    case 'acknowledged': return 'Acknowledged by support';
    case 'owner_changed': return 'Case owner changed';
    case 'follow_up': return 'Additional report added';
    case 'customer_action_requested': return 'Action requested from you';
    case 'confirmation_requested_again': return 'Confirmation requested again';
    case 'confirmation_noted': return 'Customer response noted';
    case 'partner_confirmation_noted': return 'Support provider response noted';
    case 'case_disposition': {
      // expired_unactioned (#1447): the clock closed the case with no site
      // consequence — the label must not suggest trading was touched.
      const disposition = event.new_value?.case_disposition;
      if (disposition === 'site_not_trading_ready') return 'Automatic trading paused';
      if (disposition === 'expired_unactioned') return 'Closed unactioned — your site keeps trading';
      return 'Trading readiness updated';
    }
    case 'reopened': return nextStatus
      ? `Case reopened — ${supportStatusLabel(nextStatus)}`
      : 'Case reopened';
    case 'status_changed': return nextStatus
      ? supportStatusLabel(nextStatus)
      : 'Status updated';
    default: return 'Case updated';
  }
}

/** Build an oldest-first, append-only timeline without manufacturing dates. */
export function supportCaseTimeline(report: SupportReportStatus): SupportTimelineItem[] {
  if (Array.isArray(report.events) && report.events.length) {
    return [...report.events]
      .filter((event) => !!event.occurred_at)
      .sort((a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime())
      .map((event, index) => ({
        id: String(event.id ?? `${event.event_type}-${index}`),
        label: supportEventLabel(event),
        occurred_at: event.occurred_at,
        // Only the explicitly customer-addressed, redacted action is safe to
        // render. Ledger reasons remain staff-authored free text and fail closed.
        detail: event.event_type === 'customer_action_requested'
          && typeof event.details?.action === 'string'
          ? event.details.action.trim().slice(0, 500) || null
          : null,
      }));
  }

  const fallback: SupportTimelineItem[] = [];
  if (report.created_at) {
    fallback.push({ id: 'created', label: 'Case created', occurred_at: report.created_at });
  }
  if (report.acknowledged_at) {
    fallback.push({ id: 'acknowledged', label: 'Acknowledged by support', occurred_at: report.acknowledged_at });
  }
  if (report.escalated_at) {
    fallback.push({ id: 'escalated', label: 'Escalated to Powston', occurred_at: report.escalated_at });
  }
  if (report.reopened_at) {
    fallback.push({ id: 'reopened', label: 'Case reopened', occurred_at: report.reopened_at });
  }
  if (report.resolved_at) {
    fallback.push({ id: 'resolved', label: 'Case resolved', occurred_at: report.resolved_at });
  }
  return fallback.sort(
    (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
  );
}

/** Sensitive diagnostics are never rendered for customer-role viewers. */
export function visibleSupportContext(
  report: SupportReportStatus,
  canSeeDiagnostics: boolean,
): SupportCaseContext | null {
  if (!canSeeDiagnostics || !report.context) return null;
  return report.context;
}

export function supportOwnerUpdatePayload(values: {
  route: SupportRoute;
  organisation_name: string;
  email: string;
  phone?: string;
  portal_url?: string;
}) {
  const powstonOwned = values.route === 'powston_installed' || values.route === 'self_service';
  return {
    support_route: values.route,
    support_organisation_name: powstonOwned ? 'Powston' : values.organisation_name.trim(),
    support_email: powstonOwned ? POWSTON_SUPPORT_EMAIL : values.email.trim(),
    support_phone: values.phone?.trim() || null,
    support_portal_url: values.portal_url?.trim() || null,
    powston_escalation_enabled: values.route === 'partner_managed' || values.route === 'control_only',
  };
}
